import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useState } from 'react';

import { checkGroupQuestion, type GroupQuestion, type PredictionFeedback } from './groupReactivity';

const DIAGRAM_SIZE = 220;
const DIAGRAM_CENTRE = DIAGRAM_SIZE / 2;
const NUCLEUS_RADIUS = 14;
const FIRST_SHELL_RADIUS = 28;
const MAX_SHELL_RADIUS = 100;

function polar(radius: number, degrees: number) {
  const radians = (degrees * Math.PI) / 180;
  return { x: DIAGRAM_CENTRE + radius * Math.cos(radians), y: DIAGRAM_CENTRE + radius * Math.sin(radians) };
}

/** Shell (Bohr) diagram generated from the electron arrangement; the outer shell is highlighted. */
export function ShellDiagram({
  symbol,
  atomicNumber,
  shells,
  label,
}: {
  symbol: string;
  atomicNumber: number;
  shells: number[];
  label: string;
}) {
  const step = shells.length > 1 ? (MAX_SHELL_RADIUS - FIRST_SHELL_RADIUS) / (shells.length - 1) : 0;
  const outerIndex = shells.length - 1;
  return (
    <svg viewBox={`0 0 ${DIAGRAM_SIZE} ${DIAGRAM_SIZE}`} className="mx-auto w-full max-w-[13rem]" role="img" aria-label={label}>
      {shells.map((count, shellIndex) => {
        const radius = FIRST_SHELL_RADIUS + shellIndex * step;
        const outer = shellIndex === outerIndex;
        return (
          <g key={shellIndex}>
            <circle
              cx={DIAGRAM_CENTRE}
              cy={DIAGRAM_CENTRE}
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeDasharray={outer ? undefined : '3 3'}
              className={outer ? 'text-brand-coral' : 'text-stone2'}
            />
            {Array.from({ length: count }, (_, index) => {
              const point = polar(radius, (360 * index) / count - 90);
              return (
                <circle
                  key={index}
                  cx={point.x}
                  cy={point.y}
                  r={outer ? 4.5 : 2.6}
                  className={outer ? 'fill-brand-coral' : 'fill-brand-sky'}
                />
              );
            })}
          </g>
        );
      })}
      <circle cx={DIAGRAM_CENTRE} cy={DIAGRAM_CENTRE} r={NUCLEUS_RADIUS} className="fill-ink" />
      <text x={DIAGRAM_CENTRE} y={DIAGRAM_CENTRE - 1} textAnchor="middle" className="fill-white text-[10px] font-extrabold">
        {symbol}
      </text>
      <text x={DIAGRAM_CENTRE} y={DIAGRAM_CENTRE + 9} textAnchor="middle" className="fill-white text-[7px] font-bold">
        +{atomicNumber}
      </text>
    </svg>
  );
}

export interface TrendBar {
  id: string;
  label: string;
  value: number;
  display: string;
  active?: boolean;
}

/** Horizontal bars scaled from the data; each bar carries its value as text. */
export function TrendBars({ title, unit, bars, note }: { title: string; unit: string; bars: TrendBar[]; note: string }) {
  const values = bars.map((bar) => bar.value);
  const min = Math.min(0, ...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  return (
    <figure className="rounded-[1.5rem] border border-hairline bg-white p-4">
      <figcaption className="text-xs font-extrabold uppercase tracking-wide text-slate2">
        {title} ({unit})
      </figcaption>
      <ul className="mt-3 space-y-2" aria-label={`${title} by element`}>
        {bars.map((bar) => (
          <li key={bar.id} className="grid grid-cols-[2.5rem_minmax(0,1fr)_4.5rem] items-center gap-2 text-sm">
            <span className={`font-extrabold ${bar.active ? 'text-brand-coral' : 'text-ink'}`}>{bar.label}</span>
            <span className="h-3 rounded-full bg-hairline-soft" aria-hidden="true">
              <span
                className={`block h-3 rounded-full ${bar.active ? 'bg-brand-coral' : 'bg-brand-sky'}`}
                style={{ width: `${Math.max(4, ((bar.value - min) / span) * 100)}%` }}
              />
            </span>
            <span className="text-right font-bold text-ink-soft">{bar.display}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs font-medium leading-5 text-slate2">{note}</p>
    </figure>
  );
}

export function FeedbackPanel({ label, result }: { label: string; result: PredictionFeedback }) {
  return (
    <div
      role="region"
      aria-label={label}
      className={`rounded-2xl border p-4 ${result.correct ? 'border-brand-mint/45 bg-wash-mint' : 'border-brand-coral/45 bg-wash-coral'}`}
    >
      <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
        {result.correct ? (
          <CheckCircle2 className="h-4 w-4 text-brand-mint" aria-hidden="true" />
        ) : (
          <XCircle className="h-4 w-4 text-brand-coral" aria-hidden="true" />
        )}
        {result.correct ? 'Correct' : 'Not quite'}
      </p>
      <p className="mt-1 text-sm font-medium leading-6 text-ink-soft">{result.feedback}</p>
    </div>
  );
}

function QuestionCard({ question }: { question: GroupQuestion }) {
  const [selected, setSelected] = useState('');
  const [checked, setChecked] = useState(false);
  useScienceTutorSection('QuestionCard', { selected: tutorSelection(selected), checked });
  const result = checked && selected ? checkGroupQuestion(question, selected) : null;
  return (
    <div className="rounded-[1.5rem] border border-hairline bg-white p-4 sm:p-5">
      <p className="text-sm font-extrabold leading-6 text-ink">{question.prompt}</p>
      <div className="mt-3 grid gap-2" role="group" aria-label={question.prompt}>
        {question.options.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={selected === option.id}
            onClick={() => {
              setSelected(option.id);
              setChecked(false);
            }}
            className={`rounded-2xl border px-4 py-2.5 text-left text-sm font-extrabold transition-colors motion-reduce:transition-none ${
              selected === option.id
                ? 'border-brand-coral bg-wash-coral text-ink'
                : 'border-hairline bg-white text-ink-soft hover:border-brand-coral/40'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        disabled={!selected}
        onClick={() => setChecked(true)}
        className="mt-3 rounded-full bg-ink px-5 py-2.5 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        Check answer
      </button>
      {result ? (
        <div className="mt-3">
          <FeedbackPanel label={`Feedback: ${question.id}`} result={result} />
        </div>
      ) : null}
    </div>
  );
}

export function QuestionList({ title, questions }: { title: string; questions: GroupQuestion[] }) {
  return (
    <section aria-label={title} className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">{title}</p>
      <div className="mt-3 grid gap-4 lg:grid-cols-2">
        {questions.map((question) => (
          <QuestionCard key={question.id} question={question} />
        ))}
      </div>
    </section>
  );
}
